import React from "react";
import API_BASE_URL from "./apiConfig";

const formatApiOrigin = () => {
  if (!API_BASE_URL) return "our authentication service";
  try {
    const parsed = new URL(API_BASE_URL);
    return parsed.origin;
  } catch (error) {
    return API_BASE_URL;
  }
};

const API_ORIGIN = formatApiOrigin();

const OfflineNotice = () => (
  <div>
    <p>It looks like this browser is currently offline.</p>
    <ul className="list-disc pl-4 mt-2">
      <li>Reconnect to the internet (Wi-Fi, ethernet, or hotspot).</li>
      <li>Once online, press "Continue" again to request a code.</li>
    </ul>
  </div>
);

const NetworkBlockedNotice = () => (
  <div>
    <p>We couldn't reach {API_ORIGIN}. This is usually caused by a firewall.</p>
    <ul className="list-disc pl-4 mt-2">
      <li>Ask your IT team to allow HTTPS traffic to {API_ORIGIN}.</li>
      <li>Ensure POST requests to <code>/auth/send-otp</code> are not blocked.</li>
      <li>If you're on a VPN, temporarily disconnect and try again.</li>
      <li>Try from a personal hotspot to confirm if the network is blocking it.</li>
    </ul>
  </div>
);

const TimeoutNotice = () => (
  <div>
    <p>The request to {API_ORIGIN} took too long to respond.</p>
    <ul className="list-disc pl-4 mt-2">
      <li>Check that the network connection is stable.</li>
      <li>Ask IT if slow or proxied traffic to {API_ORIGIN} is being throttled.</li>
      <li>Try again in a moment or from another connection.</li>
    </ul>
  </div>
);

const StatusBasedNotice = (status) => {
  switch (status) {
    case 400:
      return (
        <div>
          <p>The server rejected the request because the email looks invalid.</p>
          <ul className="list-disc pl-4 mt-2">
            <li>Double-check the email address for typos.</li>
            <li>Use the work email associated with your Submittal Link account.</li>
          </ul>
        </div>
      );
    case 401:
    case 403:
      return (
        <div>
          <p>Access to {API_ORIGIN} was denied.</p>
          <ul className="list-disc pl-4 mt-2">
            <li>Ask IT to allow inbound and outbound HTTPS traffic to {API_ORIGIN}.</li>
            <li>Ensure corporate security tools are not stripping required headers.</li>
          </ul>
        </div>
      );
    case 404:
      return (
        <div>
          <p>The <code>/auth/send-otp</code> endpoint was not found.</p>
          <ul className="list-disc pl-4 mt-2">
            <li>Confirm you can visit {API_ORIGIN} from the same network.</li>
            <li>If you see a 404 in the browser, share a screenshot with support.</li>
          </ul>
        </div>
      );
    case 408:
      return TimeoutNotice();
    case 429:
      return (
        <div>
          <p>Too many login codes were requested in a short period.</p>
          <ul className="list-disc pl-4 mt-2">
            <li>Wait a couple of minutes before requesting another code.</li>
            <li>If this keeps happening, contact support so we can reset the limit.</li>
          </ul>
        </div>
      );
    default:
      if (status >= 500 && status < 600) {
        return (
          <div>
            <p>Our servers returned an error ({status}).</p>
            <ul className="list-disc pl-4 mt-2">
              <li>Please try again in a moment.</li>
              <li>If the issue persists, share the timestamp with support.</li>
            </ul>
          </div>
        );
      }
      return null;
  }
};

const GenericChecklist = (email) => (
  <div>
    <p>We couldn't send a login code yet. Please review:</p>
    <ul className="list-disc pl-4 mt-2">
      {email && <li>Confirm the email {email} is spelled correctly.</li>}
      <li>Ask IT to allow HTTPS traffic to {API_ORIGIN}.</li>
      <li>Check spam or quarantine folders for emails from Submittal Link.</li>
      <li>Try from another network (mobile hotspot) to rule out firewall issues.</li>
      <li>Share this screen with your IT team if the problem continues.</li>
    </ul>
  </div>
);

export const getSendOtpErrorDescription = (error, email) => {
  if (!error) {
    return GenericChecklist(email);
  }

  const backendDetail =
    error?.response?.data?.detail?.message ||
    error?.response?.data?.detail?.error ||
    error?.response?.data?.detail;

  if (typeof backendDetail === "string" && backendDetail.trim()) {
    return backendDetail;
  }

  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return OfflineNotice();
  }

  const status = error?.response?.status;
  const statusNotice = StatusBasedNotice(status);
  if (statusNotice) {
    return statusNotice;
  }

  const code = error?.code;
  if (code === "ECONNABORTED") {
    return TimeoutNotice();
  }

  if (code === "ERR_NETWORK" || (error?.request && !error?.response)) {
    return NetworkBlockedNotice();
  }

  if (error?.message && typeof error.message === "string") {
    return (
      <div>
        <p>{error.message}</p>
        <ul className="list-disc pl-4 mt-2">
          <li>Retry the request.</li>
          <li>Share this message with support if it keeps happening.</li>
        </ul>
      </div>
    );
  }

  return GenericChecklist(email);
};

export default getSendOtpErrorDescription;
